import { CompanyPicker } from './DeviceForm';
import { usePermissions } from './permissions';
import { SearchSelect } from './SearchSelect';
import { type FormEvent, useState } from 'react';
import { ApiClientError, api } from './api';
import type { Contact } from './types';

type Props = {
  contact: Contact;
  onCancel: () => void;
  onError: (error: unknown) => void;
  onSaved: () => Promise<void>;
};

export function ContactEditForm({ contact, onCancel, onError, onSaved }: Props) {
  const can = usePermissions();
  const [companyId, setCompanyId] = useState(contact.companyId ?? '');
  const [saving, setSaving] = useState(false);

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    try {
      await api(`/api/contacts/${contact.id}`, {
        method: 'PATCH',
        revision: contact.revision,
        body: JSON.stringify({
          firstName: form.get('firstName'),
          lastName: form.get('lastName'),
          displayName: form.get('displayName'),
          jobTitle: form.get('jobTitle') || null,
          status: form.get('status'),
          ...(can('core:companies:read') ? { companyId: companyId || null } : {}),
        }),
      });
      await onSaved();
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 409) await onSaved();
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
      <label>
        <span className="label">Vorname *</span>
        <input
          name="firstName"
          required
          maxLength={100}
          defaultValue={contact.firstName}
          className="input w-full"
          autoFocus
        />
      </label>
      <label>
        <span className="label">Nachname *</span>
        <input
          name="lastName"
          required
          maxLength={100}
          defaultValue={contact.lastName}
          className="input w-full"
        />
      </label>
      <label>
        <span className="label">Anzeigename *</span>
        <input
          name="displayName"
          required
          maxLength={220}
          defaultValue={contact.displayName}
          className="input w-full"
        />
      </label>
      <label>
        <span className="label">Position</span>
        <input
          name="jobTitle"
          maxLength={200}
          defaultValue={contact.jobTitle ?? ''}
          className="input w-full"
        />
      </label>
      <label>
        <span className="label">Status</span>
        <SearchSelect name="status" defaultValue={contact.status} className="input w-full">
          <option value="ACTIVE">Aktiv</option>
          <option value="INACTIVE">Inaktiv</option>
        </SearchSelect>
      </label>
      {can('core:companies:read') && (
        <div className="md:col-span-2 xl:col-span-5">
          <CompanyPicker value={companyId} onChange={setCompanyId} />
          <p className="mt-2 text-xs text-muted">
            Der Kunde bestimmt auch geerbte Anwendungsfreigaben. Beim Wechsel werden bisherige
            aktive Kundenzuordnungen beendet.
          </p>
        </div>
      )}
      <div className="flex gap-2 md:col-span-2 xl:col-span-5">
        <button className="button" disabled={saving}>
          {saving ? 'Speichert…' : 'Änderungen speichern'}
        </button>
        <button type="button" className="button-secondary" disabled={saving} onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}
